import { addDays as fnsAddDays, addMinutes as fnsAddMinutes, differenceInDays as fnsDifferenceInDays, format, parseISO, toDate } from 'date-fns';

// Backend datetimes come back as "YYYY-MM-DD HH:mm:ss" with a space separator;
// normalize to the ISO "T" separator before parsing. parseISO reads a date-only
// "YYYY-MM-DD" as local midnight, whereas the native Date constructor reads it as
// UTC and shifts the calendar day in negative-offset timezones.
export const parseDateInput = (input: string | Date): Date =>
  typeof input === 'string' ? parseISO(input.replace(' ', 'T')) : input;

export enum DateFormat {
  'DATE_SHORT_FORMAT_SLASHES' = 'M/d/yy' /** 1/1/24 */,
  'DATE_LONG_FORMAT_SLASHES' = 'MM/dd/yyyy' /** 01/01/2024 */,
  'DATE_BASIC_SLASHES' = 'M/d/yyyy' /** 1/1/2024 */,
  'DATE_FULL_FORMAT' = 'MMMM dd, yyyy' /** January 01, 2024 */,
  'DATE_MONTH_YEAR' = 'MMM yyyy' /** Jan 2024 */,
  'DATE_MONTH_DAY' = 'MMM d' /** Jan 1 */,
  'DATE_SHORT_WITH_YEAR' = 'MMM d, yyyy' /** Jan 1, 2024 */,
  'DATE_WEEKDAY_FORMAT' = 'EEEE, LLL d' /** Monday, Jan 1 */,
  'DATE_WEEKDAY_SHORT' = 'EEE, MMM d' /** Mon, Jan 1 */,
  'DATE_WEEKDAY_SHORT_WITH_YEAR' = 'EEE, MMM d, yyyy' /** Mon, Jan 1, 2024 */,
  'DAY_SHORT' = 'EEE' /** Mon */,
  'DATETIME_WEEKDAY_FORMAT' = 'EEE, LLL dd, h:mmaaa' /** Mon, Jan 01, 1:00am */,
  'API_DATE_TIME_FORMAT' = 'yyyy-MM-dd HH:mm:ss',
  'API_DATE_FORMAT' = 'yyyy-MM-dd',
  'API_TIME_FORMAT' = 'HH:mm:ss',
  'API_JOB_DAY_TIME_FORMAT' = 'HH:mm',
  'LOCAL_DATETIME_INPUT_FORMAT' = "yyyy-MM-dd'T'HH:mm" /** HTML5 datetime-local input value */,
  'TIME_FORMAT' = 'h:mmaaa' /** 1:00am */,
  'TIME_DISPLAY_FORMAT' = 'h:mm aa',
  'DATE_WEEKDAY_TIME_FORMAT' = "EEEE, MMM d 'at' h:mm aa" /** Monday, Aug 15 at 8:30 AM */
};

/**
 * @param dateString:  ex: "2022-10-31 00:00:00"
 * @param dateFormat:  a date option from DateFormat enum
 * @returns: string: formatted string or empty string if invalid date
 */
export const formatDateFromString = (dateString: string, dateFormat: DateFormat): string => {
  const d = new Date(dateString);
  if (d.toString() === 'Invalid Date') return '';

  return format(d, dateFormat);
};

export const formatStringToDate = (dateString: string): Date => toDate(new Date(dateString));

export const formatDateToString = (date: string | Date, dateFormat: DateFormat): string => {
  const d = parseDateInput(date);

  return Number.isNaN(d.getTime()) ? '' : format(d, dateFormat);
}

/** Bucket granularity a time series is aggregated by. */
export type TimeSeriesInterval = 'DAY' | 'WEEK' | 'MONTH';

const TIME_SERIES_BUCKET_FORMATS: Record<TimeSeriesInterval, DateFormat> = {
  DAY: DateFormat.DATE_MONTH_DAY,
  WEEK: DateFormat.DATE_MONTH_DAY,
  MONTH: DateFormat.DATE_MONTH_YEAR,
};

/**
 * Label a time-series bucket for a chart axis at the granularity it was aggregated by.
 * @param date: bucket start
 * @param interval: bucket granularity
 * @returns: string: 'Jan 1' for day/week buckets, 'Jan 2024' for month buckets
 */
export const formatTimeSeriesBucket = (
  date: string | Date,
  interval: TimeSeriesInterval
): string => formatDateToString(date, TIME_SERIES_BUCKET_FORMATS[interval]);

export const getHourDifference = (startDate: Date, endDate: Date): number => Math.abs(endDate.valueOf() - startDate.valueOf()) / 36e5;

export const getTimeFromApiDate = (date: string): string => date.substring(DateFormat.API_DATE_FORMAT.length + 1);

export const sameDay = (d1: Date, d2: Date): boolean => d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

// Expects a date from our DB in the format yyyy-MM-dd HH:mm:ss - note: this does not correctly account for timezone differences.
// The Date returned from the API is in the user's timezone, but does not include that timezone information; this assumes the viewer
// is in the same timezone as the creator of the date, otherwise results will be wrong by the amount of their relative timezone offset.
export const timeSince = (date: Date): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const localDate = new Date(now.getTime() + (offset * 60000));
  const secondsBetween = Math.floor((localDate.getTime() - date.getTime()) / 1000);

  let interval = Math.floor(secondsBetween / 31536000);
  if (interval >= 1) {
    return `${interval} years ago`;
  }

  interval = Math.floor(secondsBetween / 2592000);
  if (interval >= 1) {
    return `${interval} month${interval === 1 ? '' : 's'} ago`;
  }

  interval = Math.floor(secondsBetween / 86400);
  if (interval >= 1) {
    return `${interval} day${interval === 1 ? '' : 's'} ago`;
  }

  interval = Math.floor(secondsBetween / 3600);
  if (interval >= 1) {
    return `${interval} hour${interval === 1 ? '' : 's'} ago`;
  }

  interval = Math.floor(secondsBetween / 60);
  if (interval >= 1) {
    return `${interval} minute${interval === 1 ? '' : 's'} ago`;
  }

  return 'about a minute ago';
};

// Monday, January 1
export const getDateString = (date: Date): string => {
  const formatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  } as const;

  return date.toLocaleDateString('en-US', formatOptions);
};

// Range form used on job cards / detail pages, e.g. "Mon, Jan 1, 3:00 PM -
// 5:00 PM". `end` is optional; when missing we return just the start stamp.
// Pass weekday="long" for detail pages ("Monday") vs cards' default "Mon".
export const formatDateRange = (
  start?: string | Date,
  end?: string | Date,
  weekday: 'short' | 'long' = 'short',
): string => {
  if (!start) return '';

  const s = parseDateInput(start);
  if (Number.isNaN(s.getTime())) return '';

  const opts: Intl.DateTimeFormatOptions = {
    weekday,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  if (!end) return s.toLocaleString(undefined, opts);

  const e = parseDateInput(end);
  if (Number.isNaN(e.getTime())) return s.toLocaleString(undefined, opts);

  // Same-day ranges only need the end time ("Mon, Jan 1, 3:00 PM - 5:00 PM");
  // once the range crosses midnight, dropping the end date is ambiguous
  // (is it 5:00 PM the same day, or the next one?), so spell out the full
  // end date too.
  const isSameCalendarDay = s.toDateString() === e.toDateString();
  if (isSameCalendarDay) {
    return `${s.toLocaleString(undefined, opts)} - ${e.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })}`;
  }

  return `${s.toLocaleString(undefined, opts)} - ${e.toLocaleString(undefined, opts)}`;
};

// Duration between two ISO/Date values, rounded to minute precision.
// Returns "" on bad input, "X min" under 1 hour, otherwise "Xhr" / "Xhr Ymin".
// Round total minutes once up-front so values like 59:59.x don't cascade into
// "1 hr 60 min" via a second rounding pass on the fractional hour.
export const getDurationString = (
  start?: string | Date,
  end?: string | Date,
): string => {
  if (!start || !end) return '';

  const s = parseDateInput(start);
  const e = parseDateInput(end);
  const ms = e.getTime() - s.getTime();
  if (Number.isNaN(ms) || ms <= 0) return '';

  const totalMinutes = Math.round(ms / 60000);
  const whole = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (whole < 1) return `${mins} min`;

  if (mins === 0) return `${whole} hr${whole === 1 ? '' : 's'}`;

  return `${whole} hr ${mins} min`;
};

// 2:00pm - 4:00pm
export const getTimeString = (startDate: Date, endDate: Date): string => {
  const formatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  } as const;
  const startTime = startDate.toLocaleTimeString('en-US', formatOptions).toLowerCase().replace(' ', '');
  const endTime = endDate.toLocaleTimeString('en-US', formatOptions).toLowerCase().replace(' ', '');

  return `${startTime} - ${endTime}`;
};

// Locale-aware long date with weekday and year — "Monday, January 1, 2024".
// Returns '' on null/invalid input. Uses the viewer's locale.
export const getFullDateString = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Locale-aware short date with year — "Jan 1, 2024".
// Returns '' on null/invalid input.
export const getShortDateString = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Locale-aware long date with year, no weekday — "January 1, 2024".
// Returns '' on null/invalid input.
export const getLongDateString = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Locale-aware short month + day, no year — "Jan 1". Returns '' on bad input.
export const getMonthDayString = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Locale-aware time of day — "1:00 PM" (locale-dependent). Returns '' on bad input.
export const getTimeOfDayString = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Extracts the "HH:mm" prefix from an API datetime string ("YYYY-MM-DD HH:mm:ss")
// or an ISO-with-T variant ("YYYY-MM-DDTHH:mm:ss"). Returns '' if missing.
// Lighter-weight than `getTimeFromApiDate` (which keeps seconds).
export const getShortTimeFromApiDate = (date?: string): string => {
  if (!date) return '';

  const sep = date.indexOf(' ') !== -1 ? ' ' : 'T';
  const parts = date.split(sep);
  if (parts.length < 2) return '';

  return parts[1].slice(0, 5);
};

// Re-export of date-fns addDays — local helpers in apps were duplicating this.
// "Mon, Jan 1" — short weekday + short month, no year.
export const getShortWeekdayDateString = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return format(d, DateFormat.DATE_WEEKDAY_SHORT);
};

// "Monday, Jan 1" — long weekday + short month, no year.
export const getLongWeekdayDateString = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return format(d, DateFormat.DATE_WEEKDAY_FORMAT);
};

// "Mon, Jan 1, 2024" — long-form date with short weekday prefix.
export const getWeekdayDateString = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return format(d, DateFormat.DATE_WEEKDAY_SHORT_WITH_YEAR);
};

// "Mon" — short weekday only. Caller can `.toUpperCase()` if needed.
export const getShortWeekdayString = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return format(d, DateFormat.DAY_SHORT);
};

// Compact "1h 30m" format used by booking-list rows; complements the longer
// "1 hr 30 min" form returned by getDurationString.
export const getShortDurationString = (
  start?: string | Date,
  end?: string | Date,
): string => {
  if (!start || !end) return '';

  const s = parseDateInput(start);
  const e = parseDateInput(end);
  const ms = e.getTime() - s.getTime();
  if (Number.isNaN(ms) || ms <= 0) return '';

  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;

  if (hours > 0) return `${hours}h`;

  return `${mins}m`;
};

// Numeric hours between two ISO-string datetimes, signed-but-positive.
export const getDurationHours = (
  start?: string | Date,
  end?: string | Date,
): number => {
  if (!start || !end) return 0;

  const s = parseDateInput(start);
  const e = parseDateInput(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e <= s) return 0;

  return (e.getTime() - s.getTime()) / 3600000;
};

export const isBeforeNow = (input?: string | Date): boolean => {
  if (!input) return false;

  const d = parseDateInput(input);

  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
};

export const todayYmd = (): string => format(new Date(), DateFormat.API_DATE_FORMAT);

export const getUserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const addDays = (date: Date, days: number): Date => fnsAddDays(date, days);

// Whole days between two dates, signed: positive when laterDate is after earlierDate.
// Matches moment's `.diff(other, 'days')` semantics (truncated toward zero, not rounded).
export const getDayDifference = (laterDate: Date, earlierDate: Date): number => fnsDifferenceInDays(laterDate, earlierDate);

// Re-export of date-fns addMinutes.
export const addMinutes = (date: Date, minutes: number): Date => fnsAddMinutes(date, minutes);

// HTML5 <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm") for a Date or
// parseable string. Returns '' on null/invalid input.
export const toLocalDateTimeInput = (input?: string | Date): string => {
  if (!input) return '';

  const d = parseDateInput(input);
  if (Number.isNaN(d.getTime())) return '';

  return format(d, DateFormat.LOCAL_DATETIME_INPUT_FORMAT);
};

// Convert HTML5 datetime-local value ("YYYY-MM-DDTHH:mm") to API datetime
// ("YYYY-MM-DD HH:mm:ss"). The browser strips seconds from the picker, so the
// resulting Date has seconds=0 and the API form ends in ":00". Returns
// undefined on empty/invalid input.
export const localDateTimeInputToApi = (value?: string): string | undefined => {
  if (!value) return undefined;

  const d = parseDateInput(value);
  if (Number.isNaN(d.getTime())) return undefined;

  return format(d, DateFormat.API_DATE_TIME_FORMAT);
};

// Returns the current instant formatted as "yyyy-MM-dd HH:mm:ss" in UTC,
// matching the Angular date-util's getCurrentDateString() which used moment.utc().
export const currentUtcApiDateTime = (): string => {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
};
